/**
 * The call log: who was called, what came of it, playback and transcripts.
 *
 * Three panes rather than a table with a drawer over it. A drawer that covers
 * the list is the wrong shape for this work - deciding who to call back means
 * comparing rows, and the answer is usually two rows apart from the one that is
 * open. The route carries the open call (`/calls/:id`), so a link still opens
 * the same one.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Phone } from "lucide-react";
import { cn } from "@wa/ui";
import { useT } from "../../../i18n/I18nProvider";
import { userCanPlatform, type StaffUser } from "../../../lib/auth";
import {
  callCounterpart,
  createStepTask,
  getCall,
  listCalls,
  callsCsvUrl,
  patchCall,
  retryTranscription,
  type CallDetail,
  type CallListParams,
  type CallNextStep,
  type CallRow,
} from "../../../lib/calls";
import { addPhoneSuppression } from "../../../lib/callSuppression";
import { formatPhone, normalizeE164 } from "../../../lib/phone";
import { useSplitter, useToast } from "../../ui";
import { PopHost } from "../../shell/Pop";
import { useCrmLabels } from "../../useCrmLabels";
import { usePilotChecklist } from "../../usePilotChecklist";
import { useCallContext } from "../../telephony/CallProvider";
import { useSuppressedNumbers } from "../../telephony/useSuppressedNumbers";
import { ConfirmModal } from "../_shared/ConfirmModal";
import { RailScrim, WorkFab, useRailSheet, workView } from "../_shared/workMobile";
import { useWorkDensity } from "../_shared/density";
import { CALL_FILTERS, CallRail, type CallFolder } from "./rail";
import { CallList, type CallTotals } from "./CallList";
import { CallPane } from "./CallPane";
import { CoveragePanel } from "./checklist";
import { CostPanel } from "./cost";
import { DoNotCallPanel } from "./donotcall";
import { BlocklistPanel } from "./blocklist";
import { addInboundBlock } from "../../../lib/callBlocklist";
import { readCallRoute } from "./reports";
import { useCallbackQueue } from "./useCallbackQueue";
import { DEFAULT_CALL_FILTERS, callFilterQuery, type CallFilters } from "./filters";
import { WATCH_INTERVAL_MS, WATCH_MAX_MS, isSettling } from "./watch";

const PAGE = 100;

/** The folder a link asked for, or the default. An unknown name is not an
 *  error - the query string outlives the build that wrote it. */
function askedFolder(search: string): CallFolder {
  const asked = new URLSearchParams(search).get("folder") ?? "";
  return asked in CALL_FILTERS ? (asked as CallFolder) : "all";
}

/** The strip above the list: today's volume, and how long the average call ran.
 *  Computed off the loaded page rather than fetched, so it always describes the
 *  same rows the operator is looking at. */
function totalsOf(rows: CallRow[]): CallTotals {
  const startOfDay = new Date();
  startOfDay.setHours(0, 0, 0, 0);
  const today = rows.filter((r) => new Date(r.startedAt) >= startOfDay);
  const answered = rows.filter((r) => r.durationSec);
  return {
    today: today.length,
    todaySeconds: today.reduce((s, r) => s + (r.durationSec ?? 0), 0),
    missed: rows.filter((r) => r.status === "no-answer" || r.status === "busy").length,
    averageSeconds: answered.length
      ? Math.round(answered.reduce((s, r) => s + (r.durationSec ?? 0), 0) / answered.length)
      : 0,
  };
}

export function CallsSection({ user }: { user: StaffUser }) {
  const t = useT();
  const toast = useToast();
  const navigate = useNavigate();
  const call = useCallContext();
  const suppressed = useSuppressedNumbers();
  const { labels } = useCrmLabels();
  // Once for the desk, not once per call - the pane redraws on every click in
  // the list and this registry cannot have changed between two of them.
  const { checklist } = usePilotChecklist();
  // Read off the path rather than through a nested <Routes>: a route switch
  // would remount the workspace and lose the folder, the search and the
  // splitter width every time another call is opened.
  const { pathname, search } = useLocation();
  // The three sub-routes of this section that are not a call, and the call id,
  // read as one value in `reports.ts`. A report is taken out of `openId` before
  // anything can read it: left in, "cost" is fetched as a call id, 404s, and
  // leaves the pane showing the empty state of a call that never existed.
  const { report, openId } = readCallRoute(pathname);
  // Up here rather than beside the render, because the callback queue below
  // needs it: a hook cannot be told later that it was allowed all along.
  const canCall = !!call?.enabled && userCanPlatform(user, "use:crm.calls");
  // How much is left to ring back, and the one act that changes it. Its own
  // hook for the reason `useCallDivert` is: this section already holds every
  // callback on the desk.
  const queue = useCallbackQueue(canCall);

  // Seeded from the URL, then left alone. The bell's call rows link straight at
  // a folder, and without this they would land on the unfiltered log - which is
  // the one place the operator can least see what the row was about. Seeding
  // rather than syncing on purpose: an operator who then clicks another folder
  // must not have to fight a query string they never typed.
  const [folder, setFolder] = useState<CallFolder>(() => askedFolder(search));
  const [q, setQ] = useState("");
  // Deliberately its own state and not a `CallFilters` member: it widens the
  // search rather than filtering rows, and putting it in the filter object
  // would turn the empty state into "no calls match your filters" for a search
  // that simply found nothing.
  const [deep, setDeep] = useState(false);
  // The row whose number the operator asked to mark do-not-call, held while the
  // confirmation is up.
  const [suppressing, setSuppressing] = useState<CallRow | null>(null);
  const suppressNumber = suppressing ? normalizeE164(callCounterpart(suppressing)) : null;
  const [blocking, setBlocking] = useState<CallRow | null>(null);
  const blockNumber = blocking ? normalizeE164(callCounterpart(blocking)) : null;
  /**
   * Numbers blocked from this list, this session.
   *
   * Nothing in the shell holds the blocklist - unlike the do-not-call numbers,
   * which every operator loads at sign-in to strike through - because a blocked
   * caller never reaches a screen where their number could be printed. Without
   * this set the menu item would still be offered on the next row from the same
   * robot, and pressing it again would look like the first press had failed.
   */
  const [blocked, setBlocked] = useState<ReadonlySet<string>>(() => new Set());
  const [filters, setFilters] = useState<CallFilters>(DEFAULT_CALL_FILTERS);
  const [rows, setRows] = useState<CallRow[]>([]);
  const [listBusy, setListBusy] = useState(true);
  const [detail, setDetail] = useState<CallDetail | null>(null);
  const [detailBusy, setDetailBusy] = useState(false);
  const [retrying, setRetrying] = useState(false);

  const { gridRef, railRef, leftRef, rightRef, railHandleProps, handleProps } = useSplitter({
    minRail: 168,
    minLeft: 290,
    minRight: 400,
  });
  const [density, toggleDensity] = useWorkDensity();
  const [rail, setRail] = useRailSheet(pathname);
  // Which pane a phone shows. The route already carries the open call, so this
  // needs no state of its own and the browser's back button works for free.
  const view = workView(openId);

  /**
   * The one query the screen is showing, built once.
   *
   * Hoisted out of `load` the day the export button arrived: the file and the
   * list have to be the same question, and the way that goes wrong is two
   * objects built from the same ingredients in two places, one of which gains a
   * filter a fortnight later.
   */
  // Typed rather than inferred: `deep` is `true | undefined` on the wire (never
  // `false`, which the server would read as true), and an inferred object would
  // widen it back to `boolean`.
  const listParams = useMemo<CallListParams>(
    () => ({
      ...CALL_FILTERS[folder],
      ...callFilterQuery(filters),
      q: q || undefined,
      // Never `false`: the server reads the string "false" as true.
      deep: deep && q ? true : undefined,
      pageSize: PAGE,
    }),
    [folder, q, filters, deep],
  );

  /**
   * The export asks the server the SAME question the screen just asked it.
   *
   * There used to be a fork here, because the "Recordings" folder was the one
   * filter the rail did not send: it sifted the fetched rows in the browser, so
   * an export built from `listParams` alone would have handed back every call.
   * The filter is now `CALL_FILTERS.recorded` like every other folder, so this
   * is a plain pass-through again - and it has to stay one, since a fork is
   * exactly how a file and the pane it came from start disagreeing.
   */
  const csvHref = useMemo(() => callsCsvUrl(listParams), [listParams]);

  const load = useCallback(async () => {
    setListBusy(true);
    try {
      const res = await listCalls(listParams);
      setRows(res.rows);
      return res.rows;
    } finally {
      setListBusy(false);
    }
  }, [listParams]);

  useEffect(() => {
    // A deep search is two passes over every transcript in the account (the
    // count and the page share one where clause), so it waits longer for the
    // typing to stop than an ordinary one does.
    const timer = setTimeout(() => void load(), q ? (deep ? 500 : 300) : 0);
    return () => clearTimeout(timer);
  }, [load, q, deep]);


  const loadDetail = useCallback((id: string) => {
    setDetailBusy(true);
    getCall(id)
      .then(setDetail)
      .catch(() => setDetail(null))
      .finally(() => setDetailBusy(false));
  }, []);

  /**
   * Re-read the open call WITHOUT the pane's loading state.
   *
   * This runs on a timer, and a pane that blinks through a spinner every five
   * seconds is worse than one showing a five second old row. The list row is
   * patched in place for the same reason: it draws the recording and transcript
   * badges too, and reloading the whole list would move rows under somebody who
   * is reading them for one row's worth of news.
   */
  const refreshDetail = useCallback((id: string) => {
    getCall(id)
      .then((fresh) => {
        setDetail((cur) => (cur && cur.id === fresh.id ? fresh : cur));
        setRows((cur) =>
          cur.map((r) =>
            r.id === fresh.id
              ? {
                  ...r,
                  status: fresh.status,
                  endedAt: fresh.endedAt,
                  durationSec: fresh.durationSec,
                  recordingStatus: fresh.recordingStatus,
                  recordingSec: fresh.recordingSec,
                  transcriptStatus: fresh.transcriptStatus,
                  ticket: fresh.ticket ?? r.ticket,
                }
              : r,
          ),
        );
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!openId) return;
    loadDetail(openId);
  }, [openId, loadDetail]);

  /**
   * Keep asking while the call is still being worked on somewhere else.
   *
   * Hanging up starts a chain the browser is never told about - Twilio finishes
   * the recording, the API downloads it, the queue transcribes it - and the pane
   * used to read the row exactly once, on the way in. So a transcript that
   * landed ninety seconds after the call was invisible until somebody reloaded
   * the page, which is what made a minute of work feel like ten. `isSettling`
   * owns the "is anything still coming" question; see `watch.ts` for why the
   * answer cannot just be a status check.
   */
  const watchedId = detail?.id ?? null;
  const watching = isSettling(detail, Date.now());
  useEffect(() => {
    if (!watchedId || !watching) return;
    // A budget of requests rather than a wall-clock deadline, because a hidden
    // tab must not spend it. Somebody who leaves the CRM open in a background
    // tab over lunch comes back to a call that finished ten minutes in - and a
    // clock-based ceiling would have expired unwatched, leaving the pane exactly
    // as stale as it was before any of this existed.
    let budget = Math.ceil(WATCH_MAX_MS / WATCH_INTERVAL_MS);
    const timer = setInterval(() => {
      // Nobody is looking. Costs nothing and takes nothing from the budget.
      if (document.hidden) return;
      if (budget-- <= 0) {
        clearInterval(timer);
        return;
      }
      refreshDetail(watchedId);
    }, WATCH_INTERVAL_MS);
    // Coming back to the tab is the moment the answer is wanted, so it does not
    // wait out the rest of an interval it spent hidden.
    const onVisible = () => {
      if (!document.hidden) refreshDetail(watchedId);
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      clearInterval(timer);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [watchedId, watching, refreshDetail]);

  // Nothing open yet: show the newest call rather than an empty pane.
  const autoOpened = useRef<string | null>(null);
  useEffect(() => {
    if (openId || !rows.length) return;
    const first = rows[0]!.id;
    if (autoOpened.current === first) return;
    autoOpened.current = first;
    loadDetail(first);
  }, [openId, rows, loadDetail]);

  // A finished call belongs at the top of the list, where a real one would be.
  const phase = call?.state.phase;
  const endedId = call?.state.callLogId ?? null;
  useEffect(() => {
    if (phase !== "ended") return;
    void load();
    // And the row itself, which the pane may be holding from BEFORE the hangup:
    // no end time, no duration, and both status columns still reading as though
    // the call were in progress. Nothing would start the watch off a row like
    // that. Safe to call blind - it patches the pane only when that call is the
    // one open, and the list row either way.
    if (endedId) refreshDetail(endedId);
  }, [phase, endedId, load, refreshDetail]);

  /**
   * The queue shrinks without anybody clicking anything.
   *
   * An outgoing call that reaches somebody closes whatever waiting calls that
   * number had (the API's `lib/callback.ts`), which is the whole reason this
   * folder drains rather than fills. Nothing tells the browser: it happens on a
   * Twilio status callback that lands after the operator has hung up.
   *
   * Keyed on the call window going back to IDLE rather than on "ended", and the
   * difference is the race. "ended" is the moment the SDK sees the hangup, which
   * is the same moment Twilio is only just posting the report that closes the
   * row - a count read then would be read one query too early. Idle is when the
   * operator dismisses the window, comfortably after.
   *
   * Guarded on having seen a call at all, so mounting the desk does not ask for
   * a number the hook has just fetched.
   */
  const sawCall = useRef(false);
  useEffect(() => {
    if (phase && phase !== "idle") sawCall.current = true;
    else if (sawCall.current) {
      sawCall.current = false;
      queue.refresh();
    }
  }, [phase, queue]);

  /**
   * The fields an operator owns, all of which the PATCH answers with the full
   * row. Writing that answer straight into state rather than re-fetching keeps
   * the pane from blinking through its loading state on every keystroke's worth
   * of saved note.
   *
   * A label change is the exception and does reload the list. It has to: the
   * middle pane draws the same labels, and with a label filter on, taking that
   * label off a call must take the call out of the list. A note is typed and
   * saved continuously, so it stays on the no-reload path, and so does a ticked
   * next step - the list does not draw those at all.
   */
  const patchRow = useCallback(
    async (
      id: string,
      body: {
        clientId?: string | null;
        leadId?: string | null;
        contactId?: string | null;
        note?: string | null;
        tags?: string[];
        nextSteps?: CallNextStep[];
      },
    ) => {
      try {
        const updated = await patchCall(id, body);
        // Only when it is the open one. The right-click menu acts on any row in
        // the list, and writing its answer into the pane would swap the call the
        // operator is reading for one they only labelled in passing.
        //
        // Merged over what the pane holds rather than swapped for it: PATCH
        // answers with the row, and the row alone carries no `liveTurns`, so a
        // note saved while a transcript is still being made would otherwise take
        // the live preview off the screen with it.
        setDetail((cur) => (cur && cur.id === id ? { ...cur, ...updated } : cur));
        if (body.tags !== undefined) await load();
      } catch {
        toast(t("common.error"));
      }
    },
    [load, t, toast],
  );

  async function patch(body: {
    clientId?: string | null;
    leadId?: string | null;
    contactId?: string | null;
    note?: string | null;
    tags?: string[];
    nextSteps?: CallNextStep[];
  }) {
    if (!detail) return;
    await patchRow(detail.id, body);
  }

  /**
   * Tick a waiting call off the callback queue, or put it back.
   *
   * Not `patchRow`: this is its own endpoint with its own refusal (a call the
   * queue would never show is a 409, not a silent no-op), and it answers with
   * the queue's fresh size rather than with the row.
   *
   * The list is reloaded, always. In the callbacks folder the row has just left
   * the query and has to go; in every other folder it has gained a tag the tile
   * draws. Patching the held row in place would be cheaper and would be wrong
   * in the folder this feature is for.
   */
  const markHandled = useCallback(
    async (row: CallRow, handled: boolean) => {
      const at = await queue.setHandled(row.id, handled);
      if (at === undefined) return;
      setDetail((cur) => (cur && cur.id === row.id ? { ...cur, handledAt: at } : cur));
      await load();
    },
    [load, queue],
  );

  /**
   * File one of the call's next steps as a task.
   *
   * Re-reads the detail rather than merging the answer in: the response carries
   * the new binding, but `stepTasks` is what the row is drawn from and that is
   * assembled by the detail route.
   */
  async function fileStepTask(text: string, dueDate: string | null) {
    if (!detail) return;
    try {
      await createStepTask(detail.id, { text, dueDate });
      loadDetail(detail.id);
      toast(t("calls.detail.taskMade"));
    } catch {
      toast(t("common.error"));
    }
  }

  async function retry() {
    if (!detail) return;
    setRetrying(true);
    try {
      await retryTranscription(detail.id);
      loadDetail(detail.id);
      toast(t("calls.detail.retryQueued"));
    } finally {
      setRetrying(false);
    }
  }

  const totals = useMemo(() => totalsOf(rows), [rows]);

  return (
    // PopHost so the call pane's overflow menu has the same single-open-slot
    // behaviour as the ones in the header.
    <PopHost>
      {/* `is-dense` is how the compact density reaches everything written in
          `crm-` CSS. The values CSS cannot reach from here - a Tailwind utility
          already on an element - read the same setting through `useWorkCompact`.
          The setting is shared with the mail screen: same three-pane shape, same
          habit. */}
      {/* `data-view` is the phone layout's whole switch: it says which of the
          two panes is on screen, and index.css hides the other. It is written
          at every width because the rule that reads it is inside a media query -
          one breakpoint, in CSS, rather than the same number in two places. */}
      {/* `is-single` drops the middle column and the divider: both the
          do-not-call screen and the two reports are one panel with nothing to
          open beside them, and a splitter over an empty pane is a handle that
          resizes nothing. */}
      <div
        className={cn("crm-work", density === "compact" && "is-dense", report && "is-single")}
        // Always "list" on those routes. There is no detail pane to switch to,
        // and a phone left on "detail" would show an empty screen.
        data-view={report ? "list" : view}
        ref={gridRef}
      >
        <RailScrim open={rail} onClose={() => setRail(false)} />

        <CallRail
          ref={railRef}
          open={rail}
          onPicked={() => setRail(false)}
          folder={folder}
          onFolder={(next) => {
            setFolder(next);
            // A folder is a view of the log, so picking one has to come back
            // from either of the two screens that are not the log - otherwise
            // the rail highlights a folder whose rows are nowhere on screen.
            if (report) navigate("/calls");
          }}
          labels={labels.calls}
          tag={filters.tag}
          onTag={(tag) => setFilters((f) => ({ ...f, tag }))}
          onDial={() => call?.openDialer()}
          canCall={canCall}
          report={report}
          reach={call?.reach ?? "off"}
          callbacks={queue.count}
        />

        {/* The rail's own divider, in the gutter on its right edge. Rendered on
            the report screens too, unlike the one between the panes: those
            screens still have a rail beside a column, so its width is still a
            choice. It hides at the same breakpoint - below 1000px the workspace
            is one column and there is no edge to drag. */}
        <button type="button" title={t("common.resizeRail")} aria-label={t("common.resizeRail")} {...railHandleProps}>
          <i className="crm-handle-line" />
        </button>

        {report === "cost" ? (
          <CostPanel onRail={() => setRail(true)} />
        ) : report === "coverage" ? (
          <CoveragePanel onRail={() => setRail(true)} />
        ) : report === "blocklist" ? (
          // One right for both directions, unlike the screen below it. A
          // blocked customer is unreachable with no error either side ever
          // sees, so the cure may not need a wider key than the mistake did.
          <BlocklistPanel
            canManage={userCanPlatform(user, "use:crm.calls")}
            onRail={() => setRail(true)}
          />
        ) : report === "do-not-call" ? (
          <DoNotCallPanel
            canAdd={userCanPlatform(user, "use:crm.calls")}
            canRemove={userCanPlatform(user, "manage:crm.calls")}
            onRail={() => setRail(true)}
            // Kept in step with the set the whole shell holds, so a number
            // marked or released here is struck through - or no longer struck
            // through - on every other screen without a reload.
            onAdded={(value) => suppressed?.add(value)}
            onRemoved={(value) => suppressed?.remove(value)}
          />
        ) : (
          <>
          <CallList
            ref={leftRef}
            rows={rows}
            openId={detail?.id ?? null}
            loading={listBusy}
            q={q}
            onQ={setQ}
            deep={deep}
            onDeep={() => setDeep((on) => !on)}
            csvHref={csvHref}
            filters={filters}
            onFilters={setFilters}
            onOpen={(id) => navigate(`/calls/${id}`)}
            totals={totals}
            labels={labels.calls}
            canCall={canCall}
            onCallBack={(row) =>
              call?.startCall({
                // Them, not the column: an inbound row's `toNumber` is our own
                // Twilio line, so this used to ring the office back.
                phone: callCounterpart(row),
                companyName: row.companyName ?? undefined,
                regCode: row.regCode ?? undefined,
                leadDomain: row.leadDomain ?? undefined,
                source: "manual",
              })
            }
            onSuppress={setSuppressing}
            onBlock={setBlocking}
            blocked={blocked}
            onPatch={patchRow}
            onHandled={markHandled}
            density={density}
            onDensity={toggleDensity}
            onRail={() => setRail(true)}
          />

          <button type="button" title={t("common.resize")} aria-label={t("common.resize")} {...handleProps}>
            <i className="crm-handle-line" />
          </button>

          <div ref={rightRef} className="crm-work-pane">
            <CallPane
              call={detail}
              loading={detailBusy && !detail}
              labels={labels.calls}
              checklist={checklist}
              retrying={retrying}
              onRetry={retry}
              onCallBack={(number, companyName) => call?.startCall({ phone: number, companyName, source: "manual" })}
              onPatch={patch}
              onHandled={(handled) => detail && void markHandled(detail, handled)}
              onTask={fileStepTask}
              canMakeTask={userCanPlatform(user, "manage:crm.projects")}
              // The browser mirror of `mayHearCall` on the server: own call, OR
              // the supervisor key. Computed rather than assumed from the
              // permission alone, because a plain operator may correct their own
              // calls - and drawing a pencil that only ever returns 403 is worse
              // than drawing none.
              canHear={
                userCanPlatform(user, "read:crm.calls.recordings") ||
                (!!detail && !!user && detail.createdBy === user.id)
              }
              canMoveLead={userCanPlatform(user, "manage:crm.clients")}
              canReadClients={userCanPlatform(user, "view:crm")}
              // Written into the copy the pane is drawing rather than refetched:
              // the lead's row is another module's, and the call itself did not
              // change.
              onLeadMoved={(status) =>
                setDetail((cur) => (cur && cur.lead ? { ...cur, lead: { ...cur.lead, status } } : cur))
              }
              onBack={() => navigate("/calls")}
              // An erased recording leaves the row; an erased call does not, so
              // one of the two also has to leave the pane. Both re-read the list,
              // because the row tile carries the recording state as well.
              onErased={(scope) => {
                void load();
                // An erased recording leaves the row, so the pane re-reads it; an
                // erased call does not, so the pane has to leave.
                if (scope === "call") navigate("/calls");
                else if (openId) loadDetail(openId);
              }}
              // Same repair as an erased recording and a different event: the
              // row the pane is holding has a stale `recordingStatus`, and the
              // list shows the audio pill off the same column.
              onRecordingFetched={() => {
                void load();
                if (openId) loadDetail(openId);
              }}
            />
          </div>
          </>
        )}

        {/* Only when this operator may place one. The rail hides its own Dial
            button on the same test, and a phone that offers to call and then
            answers 403 is worse than one that never offered. */}
        {canCall ? (
          <WorkFab label={t("calls.dial.title")} onClick={() => call?.openDialer()}>
            <Phone className="size-5" />
          </WorkFab>
        ) : null}

        {/* Confirmed here rather than in the row menu, which unmounts on the
            click that opens this. The entry it writes is one nobody can remove
            afterwards, so it is worth the second click. */}
        {suppressing ? (
          <ConfirmModal
            title={t("calls.suppression.markTitle")}
            body={t("calls.suppression.markBody", { phone: formatPhone(suppressNumber ?? "") })}
            confirmLabel={t("calls.suppression.mark")}
            cancelLabel={t("common.cancel")}
            onConfirm={async () => {
              if (!suppressNumber) return;
              const res = await addPhoneSuppression({ phone: suppressNumber, source: "unsubscribe" }).catch(
                () => null,
              );
              if (res) suppressed?.add(res.suppression.value);
              setSuppressing(null);
            }}
            onClose={() => setSuppressing(null)}
          />
        ) : null}

        {/* Confirmed here for the same reason as the one above: the row menu
            unmounts on the click that opens this. The second click is worth it
            for a different reason, though. This entry CAN be undone - the harm
            is that nothing will ever say it needs undoing. A blocked caller is
            refused before the call is answered, so a number blocked by mistake
            just goes quiet. */}
        {blocking ? (
          <ConfirmModal
            title={t("calls.blocklist.markTitle")}
            body={t("calls.blocklist.markBody", { phone: formatPhone(blockNumber ?? "") })}
            confirmLabel={t("calls.blocklist.mark")}
            cancelLabel={t("common.cancel")}
            onConfirm={async () => {
              if (!blockNumber) return;
              const res = await addInboundBlock({ phone: blockNumber, source: "spam" }).catch(() => null);
              // The value the SERVER stored, never the one we sent. The two are
              // the same today and the server is the one that canonicalises.
              if (res) setBlocked((cur) => new Set(cur).add(res.block.value));
              setBlocking(null);
            }}
            onClose={() => setBlocking(null)}
          />
        ) : null}
      </div>
    </PopHost>
  );
}
